/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  DocumentID,
  DocumentIDValue,
  DocumentValue,
  Store,
} from '@dittolive/ditto'

export type User = {
  id: string
  email: string
  firstName?: string | null
}

export type Actor = User

export enum IssueStatus {
  open = 'open',
  closed = 'closed',
}

export type IssueLabel = {
  name: string
  description: string
  color: string
  bgColor: string
  createdAt: number
  updatedAt?: number
}

export interface Event {
  readonly type: string
  readonly createdAt: number
  readonly actorId: string
  readonly id: string
}

export interface ClosedEvent extends Event {
  type: 'ClosedEvent'
}

export interface ReopenedEvent extends Event {
  type: 'ReopenedEvent'
}

export interface UpdatedFieldEvent extends Event {
  type: 'UpdatedFieldEvent'
  name: string
  from: any
  to: any
}

export interface NewCommentEvent extends Event {
  type: 'NewCommentEvent'
  commentId: string
}

export interface CommentUpdatedEvent extends Event {
  type: 'CommentUpdatedEvent'
}

export interface CommentDeletedEvent extends Event {
  type: 'CommentDeletedEvent'
}

export interface UpdatedCommentEvent extends Event {
  type: 'UpdatedCommentEvent'
}

export type IssueCommentToCreate = {
  id: string
  body: string
  authorId: string
  createdAt: number
  updatedAt?: number
}

export type IssueComment = {
  readonly id: string
  readonly authorId: string
  readonly createdAt: number
  body: string
  updatedAt?: number
}

export type IssueTimeline =
  | ClosedEvent
  | UpdatedFieldEvent
  | ReopenedEvent
  | CommentDeletedEvent
  | UpdatedCommentEvent

export type IssueToCreate = {
  id: string
  title: string
  status: IssueStatus
  comments: Array<IssueComment>
  authorId: string
  createdAt: number
  updatedAt?: number
  closedAt?: number
  appId?: string
  organizationId?: string | null
  body: string
  labels: Array<IssueLabel>
  platform: string | undefined
  sdkVersion: string | undefined
  timeline: Array<IssueTimeline>
}

export type Issue = {
  readonly id: string
  readonly authorId: string
  readonly createdAt: number
  readonly appId: string
  readonly organizationId?: string
  title: string
  status: IssueStatus
  comments: Array<IssueComment>
  updatedAt?: number
  closedAt?: number
  body: string
  labels: Array<IssueLabel>
  platform: string | undefined
  sdkVersion: string | undefined
  timeline: Array<IssueTimeline>
}

export type WebhookToCreate = {
  collectionName: string
  liveQuery: string
  url: string
}

export type Webhook = {
  collection_name: string
  filter_expression: string
  url: string
}

export type WebhookWithId = Webhook & {
  id: DocumentID
}

export type WebhookApiSecretsDoc = {
  secrets: Array<WebhookApiSecretDoc>
}

export type WebhookApiSecretDoc = {
  base64_encoded_value: string
  expiration: string | undefined
}

export interface AppConfig {
  appId: string
  organizationId: string | null
}

export interface DittoIssuesManagementClientConstructor {
  new (
    store: Store,
    actor: Actor,
    config?: AppConfig,
  ): DittoIssuesManagementClient
}

export interface DittoIssuesManagementClient {
  readonly store: Store
  readonly actor: Actor
  /** Creates new issue object in the store and returns the document with the id and value. */
  createIssue(
    title: string,
    body: string,
    platform: string,
    sdkVersion: string,
  ): Promise<DocumentIDValue>
  /** Updates issue title */
  updateTitle(id: string, title: string): Promise<DocumentValue>
  /** Updates issue description */
  updateDescription(id: string, description: string): Promise<DocumentValue>
  /** Updates the issue platform field value */
  updatePlatform(id: string, platform: string): Promise<DocumentValue>
  /** Updates issue SDK version */
  updateSdkVersion(id: string, sdkVersion: string): Promise<DocumentValue>
  /** Adds a new comment */
  addComment(
    id: string,
    body: string,
    options?: { shouldClose?: boolean; shouldReopen?: boolean },
  ): Promise<DocumentValue>
  /** Reopen issue */
  reopenIssue(id: string): Promise<DocumentValue>
  /** Close issue */
  closeIssue(id: string): Promise<DocumentValue>
}
